import React from "react";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Paper, Typography } from "@mui/material";
import type { dailyBrief } from "store/types/news";
import { formatNewsDate } from "utils/format-news-date";

type Props = {
  briefing: dailyBrief | null;
  onOpenFull?: () => void;
};

export const BriefingCard: React.FC<Props> = ({ briefing, onOpenFull }) => {
  const body = () => {
    if (briefing?.highlights.length) {
      return (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: "4px" }}>
          {briefing?.highlights.map((highlight, i) => (
            <Box
              component="li"
              key={i}
              sx={{
                fontSize: 12,
                lineHeight: 1.55,
                color: "rgba(255,255,255,0.82)",
                pl: "13px",
                position: "relative",
                "&::before": {
                  // eslint-disable-next-line quotes
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: "7px",
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  bgcolor: "#c09560"
                }
              }}
            >
              {highlight}
            </Box>
          ))}
        </Box>
      );
    }
    if (briefing?.summary) {
      return (
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: 1.65,
            color: "rgba(255,255,255,0.82)",
            mb: "4px",
            whiteSpace: "pre-wrap"
          }}
        >
          {briefing?.summary}
        </Typography>
      );
    }
    return (
      <Typography sx={{ fontSize: 12, lineHeight: 1.65, color: "rgba(255,255,255,0.72)" }}>
        No briefing available yet. Use Feed Settings to configure topics.
      </Typography>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        mx: { xs: 0, sm: 0 },
        borderRadius: "6px",
        p: "13px 15px",
        bgcolor: "#071f35",
        backgroundImage: "none",
        boxShadow: "none"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "9px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AutoAwesomeIcon sx={{ fontSize: 14, color: "#c09560" }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: "0.04em",
              color: "#fff"
            }}
          >
            Daily briefing
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>
          AI-generated · {formatNewsDate(briefing?.date, { absoluteOnly: true })}
        </Typography>
      </Box>

      {body()}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: "10px",
          pt: "8px",
          borderTop: "1px solid rgba(255,255,255,0.1)",
          gap: 1
        }}
      >
        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.35)", flex: 1, minWidth: 0, m: 0, p: 0 }}>
          This summary is generated from news sources and does not constitute investment advice.
        </Typography>
        {onOpenFull && (
          <Button
            type="button"
            variant="text"
            disableRipple
            onClick={onOpenFull}
            startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
            sx={{
              flexShrink: 0,
              ml: "12px",
              mt: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              fontSize: 11,
              fontWeight: 600,
              color: "rgba(255,255,255,0.5)",
              textTransform: "none",
              p: 0,
              minWidth: 0,
              fontFamily: "inherit",
              "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
              "&:hover": {
                color: "rgba(255,255,255,0.85)",
                bgcolor: "transparent",
                "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.85)" }
              },
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 }
            }}
          >
            View full brief
          </Button>
        )}
      </Box>
    </Paper>
  );
};
